const _ = require('underscore');
const Q = require('q');
const { get, set, remove, clear } = require('model/stores/indexedDbStore');
const model = require('model/model');
const productService = require('services/productService');
const assignmentService = require('services/assignmentService');
const { uploadOfflineEvents } = require('core/analytics');
const config = require('core/config');
const MEDIA_HOST = config.resources.media.cdn || config.resources.media.origin;
const ASSET_HOST = config.resources.assets.cdn || config.resources.assets.origin;
const { v4: uuidv4 } = require('uuid');
const urlUtilities = require('utilities/urlUtilities');
const msgDeferred = {};
const logger = require('utilities/logger');

let available = 'serviceWorker' in navigator && 'indexedDB' in window;
let serviceWorkerInstalled = false;
let serviceWorkerUpdated = false;

const { events, fireEvent } = require('utilities/analyticsUtilities');

const getOfflineModeSetting = () => {
	if (!available) {
		model.set('offlineMode', false);
		return Q();
	}
	return get('offlineMode')
		.then(mode => {
			if (typeof mode === 'undefined') {
				mode = false;
				set('offlineMode', mode);
			}
			return mode;
		})
		.catch(e => {
			model.set('offlineMode', false);
			return false;
		});
};

const sendOfflineMessage = msg => {
	const userContext = model.get('userContext');
	const token = userContext ? userContext.token : undefined;
	const uid = userContext ? userContext.uid : undefined;
	const version = window.readworks.version !== '0' ? window.readworks.version : undefined;

	if (available) {
		navigator.serviceWorker.ready.then(registration => {
			// console.log('client sending message', msg.type);
			registration.active.postMessage(
				_.assign(
					{
						token,
						uid,
						version,
						MEDIA_HOST,
						ASSET_HOST
					},
					msg
				)
			);
		});
	}
};

const readOfflineMessage = msg => {
	let mode = model.get('offlineMode');
	// console.log('client received message', msg.type, msg);

	if (msg) {
		if (msg.type === 'ack') {
			// console.log('ack', msg.id, msg.action);
			if (msgDeferred[msg.id] && msg.action) {
				msgDeferred[msg.id][msg.action]();
			}
		} else if (msg.type === 'event') {
			if (msg.name === 'CACHE_RESPONSE' && mode === false) {
				fireEvent('ERRANT_CACHE_RESPONSE', { r: msg.route });
			}
		}

		if (msg.err) {
			logger.error(msg.err);
		}

		if (msgDeferred && msgDeferred[msg.id]) {
			delete msgDeferred[msg.id];
		}
	}
};

const sendOfflineModeToWorker = mode => {
	return sendOfflineMessage({ type: 'offline-mode', mode });
};

const registerServiceWorker = () => {
	let deferred = Q.defer();
	let prms = deferred.promise;
	if (available) {
		let workerVersion = urlUtilities.getAssetVersion('offline/offline-worker.js');
		navigator.serviceWorker.register('/offline-worker.js?v=' + workerVersion).then(
			registration => {
				// Registration was successful
				// console.log('ServiceWorker registration successful with scope: ', registration.scope);
				serviceWorkerInstalled = true;
				serviceWorkerUpdated = true;
				navigator.serviceWorker.addEventListener('message', event => {
					readOfflineMessage(event.data);
				});
				getOfflineModeSetting().then(mode => sendOfflineModeToWorker(mode));
				deferred.resolve();
			},
			function (err) {
				// registration failed :(
				deferred.reject();
				// console.log('ServiceWorker registration failed: ', err);
			}
		);
	} else {
		deferred.reject();
	}

	return prms;
};

// Ensure SW is installed.
// Used on Download && Enable Offline
const checkForServiceWorker = () => {
	let deferred = Q.defer();
	let prms = deferred.promise;
	if (available && !serviceWorkerInstalled) {
		prms = registerServiceWorker();
	} else if (!available) {
		deferred.reject(false);
	} else {
		deferred.resolve(serviceWorkerInstalled);
	}

	return prms;
};

// Ensure every page load checks for new SW
// If initial register, serviceWorkerUpdated should already be TRUE
const updateServiceWorkerIfInstalled = () => {
	if (available) {
		navigator.serviceWorker.ready.then(registration => {
			if (!serviceWorkerUpdated) {
				registerServiceWorker();
			}
		});
	}
};

const createAssignmentMessage = assignment => {
	const userContext = model.get('userContext');

	return productService.get(assignment.p).then(p => {
		let assets = [];
		if (p.content) {
			assets =
				$('img', p.content)
					.toArray()
					.map(i => i.src) || [];
		}
		if (p.img) {
			assets.push(p.img);
		}
		if (p.articles) {
			Object.keys(p.articles).forEach(pid => {
				let article = p.articles[pid];
				if (article.img) {
					assets.push(article.img);
				}
				let bodyImgs =
					$('img', article.content)
						.toArray()
						.map(i => i.src) || [];
				if (bodyImgs && bodyImgs.length) {
					assets = assets.concat(bodyImgs);
				}
			});
		}
		if (assignment.opts) {
			// Take into account extra assignment options - Challenge, Support
			if (assignment.opts.challenge) {
				Object.keys(p.supplemental_texts.challenge).forEach(pid => {
					let article = p.supplemental_texts.challenge[pid];
					assets.push(article.img);
					let bodyImgs =
						$('img', article.content)
							.toArray()
							.map(i => i.src) || [];
					if (bodyImgs && bodyImgs.length) {
						assets = assets.concat(bodyImgs);
					}
				});
			}
			if (assignment.opts.support) {
				Object.keys(p.supplemental_texts.support).forEach(pid => {
					let article = p.supplemental_texts.support[pid];
					assets.push(article.img);
					let bodyImgs =
						$('img', article.content)
							.toArray()
							.map(i => i.src) || [];
					if (bodyImgs && bodyImgs.length) {
						assets = assets.concat(bodyImgs);
					}
				});
			}
			if (assignment.opts.va && assignment.opts.va.length) {
				assets = assets.concat(p.getAllWordAudioUrls() || []);
			}
		}
		return _.assign(
			{},
			{
				aid: assignment.aid,
				cc: assignment.cc,
				vpid: assignment.p,
				assets,
				uid: userContext.uid
			},
			assignment
		);
	});
};

const setAssignmentData = message => {
	let uid = message.uid;
	let aid = message.aid;

	if (!available) {
		return Q();
	}

	return get('offlineAssignments').then(offlineAssignments => {
		offlineAssignments = offlineAssignments || {};
		let uidAssignments = offlineAssignments[uid] || {};
		uidAssignments = _.assign({}, uidAssignments, {
			[aid]: message
		});
		offlineAssignments = _.assign({}, offlineAssignments, {
			[uid]: uidAssignments
		});
		return set('offlineAssignments', offlineAssignments);
	});
};

const getAssignmentData = aid => {
	let userContext = model.get('userContext');

	if (!userContext || !available) {
		return Q.reject();
	}

	let uid = userContext.uid;
	return get('offlineAssignments').then(offlineAssignments => {
		let assignment = null;
		if (offlineAssignments && offlineAssignments[uid] && offlineAssignments[uid][aid]) {
			assignment = offlineAssignments[uid][aid];
		}
		return assignment;
	});
};

const updateOfflineAssignment = assignment => {
	return Q.all([createAssignmentMessage(assignment), getAssignmentData(assignment.aid)]).spread((message, data) => {
		let prms = Q();
		// ensure any workspace data is carried over
		if (data && message) {
			if (data.bok || data.submitData || data.answers || data.va) {
				message = _.assign(message, {
					submitData: data.submitData,
					answers: data.answers,
					bok: data.bok,
					va: data.va
				});
			}
			// console.log('updating', message.aid);
			prms = setAssignmentData(message);
		}
		return prms;
	});
};

const updateOfflineAssignments = assignments => {
	let updatePrms = Q();
	if (assignments && assignments.length) {
		assignments.forEach(a => {
			updatePrms = updatePrms.then(() => {
				return updateOfflineAssignment(a);
			});
		});
	}
	return updatePrms;
};

const deleteAssignmentData = message => {
	let uid = message.uid;
	let aid = message.aid;

	if (!available) {
		return Q();
	}

	return get('offlineAssignments').then(offlineAssignments => {
		if (offlineAssignments && offlineAssignments[uid]) {
			let uidAssignments = offlineAssignments[uid];
			delete uidAssignments[aid];

			offlineAssignments = _.assign({}, offlineAssignments, {
				[uid]: _.assign({}, uidAssignments)
			});
		}
		return set('offlineAssignments', offlineAssignments);
	});
};

const getOfflineUserContext = () => {
	if (!available) {
		return Q(null);
	}
	return get('offlineUserContext');
};

const removeOfflineUserContext = () => {
	if (!available) {
		return Q();
	}
	return remove('offlineUserContext');
};

const getOfflineAssignments = () => {
	if (!available) {
		return Q({});
	}
	return get('offlineAssignments');
};

const getAllOfflineAssignmentData = () => {
	const userContext = model.get('userContext');
	if (!userContext) {
		return Q([]);
	}
	if (!available) {
		return Q([]);
	}
	const uid = userContext.uid;
	const classCode = userContext.classCode;
	return getOfflineAssignments().then(offlineAssignments => {
		let assignments = {};
		let assignmentsList = [];
		if (offlineAssignments && offlineAssignments[uid]) {
			let allAssignments = offlineAssignments[uid];
			assignments = Object.keys(allAssignments).reduce((acc, aid) => {
				if (allAssignments[aid].cc === classCode) {
					acc[aid] = allAssignments[aid];
				}
				return acc;
			}, {});
		}
		if (Object.keys(assignments).length) {
			Object.keys(assignments).forEach(aid => {
				assignmentsList.push(assignments[aid]);
			});
		}

		return assignmentsList;
	});
};

const getOfflineWorkspaceData = () => {
	let data = null;
	let userContext = model.get('userContext');

	if (!userContext || !available) {
		return Q(data);
	}

	let uid = userContext.uid;
	return get('offlineWorkspaceData').then(wsData => {
		if (wsData) {
			data = wsData[uid];
		}
		return data;
	});
};

const setOfflineWorkspaceData = value => {
	let data = null;
	let userContext = model.get('userContext');

	if (!userContext || !available) {
		return Q(data);
	}
	let uid = userContext.uid;
	return get('offlineWorkspaceData').then(wsData => {
		let valueObj = {
			[uid]: value
		};
		if (wsData) {
			wsData = _.assign({}, wsData, valueObj);
		} else {
			wsData = valueObj;
		}
		return set('offlineWorkspaceData', wsData).then(() => value);
	});
};

const checkForOfflineWorkspaceData = () => {
	let userContext = model.get('userContext');

	if (!userContext || !available) {
		return Q();
	}

	let uid = userContext.uid;
	return Q.all([getAllOfflineAssignmentData(), get('offlineEvents')]).spread((offlineAssignments, eventData) => {
		let value = false;
		let userEvents = eventData ? eventData[uid] : null;
		if (userEvents && userEvents.length) {
			value = true;
		} else {
			value = offlineAssignments.some(a => a.bok || a.answers || a.submitData || a.va);
		}
		return setOfflineWorkspaceData(value);
	});
};

const checkCore = () => {
	let deferred = Q.defer();
	let prms = deferred.promise;
	let message = {
		type: 'check-core',
		id: uuidv4()
	};

	msgDeferred[message.id] = deferred;
	sendOfflineMessage(message);

	return prms;
};

const downloadAssignment = assignment => {
	let deferred = Q.defer();
	// console.log('DOWNLOAD!!!', assignment.aid);
	return checkForServiceWorker()
		.then(() => checkCore())
		.then(() => createAssignmentMessage(assignment))
		.then(message => {
			let prms = deferred.promise;
			message.type = 'assignment';
			message.id = uuidv4();
			msgDeferred[message.id] = deferred;
			sendOfflineMessage(message);
			fireEvent(events.DOWNLOAD_ASSIGNMENT, {
				aid: message.aid
			});
			return prms.then(() => setAssignmentData(message));
		});
};

const updateOfflineWorkspace = aid => {
	const userContext = model.get('userContext');
	const uid = userContext ? userContext.uid : undefined;
	const assignments = model.get('offlineAssignments'); // Quick check for assignments
	if (!userContext || !available) {
		return Q();
	}
	let deferred = Q.defer();
	let prms = deferred.promise;
	let message = {
		type: 'update-cache',
		id: uuidv4(),
		urls: [`/v2/assignment/studentOpenWorkspace?aid=${aid}`]
	};
	let savedAssignments = uid && assignments ? assignments[uid] : null;

	// Ensure user has saved some assignments
	if (savedAssignments && Object.keys(savedAssignments).length) {
		getAllOfflineAssignmentData().then(assignmentsList => {
			let isSaved = false;
			// Check to see if this AID is saved
			if (assignmentsList && assignmentsList.length) {
				isSaved = assignmentsList.some(a => a.aid === aid);
			}
			if (isSaved) {
				msgDeferred[message.id] = deferred;
				sendOfflineMessage(message);
			} else {
				deferred.resolve();
			}
		});
	} else {
		deferred.resolve();
	}
	return prms;
};

const clearAssignment = assignment => {
	// console.log('CLEAR!!!', assignment.aid);
	return createAssignmentMessage(assignment).then(message => {
		message.type = 'clear-assignment';
		sendOfflineMessage(message);
		fireEvent(events.CLEAR_ASSIGNMENT, {
			aid: message.aid
		});
		return deleteAssignmentData(message).then(() => checkForOfflineWorkspaceData());
	});
};

const clearOldAssignments = () => {
	return getAllOfflineAssignmentData().then(offlineAssignmentData => {
		let now = Date.now() / 1000;
		let prms = Q();
		offlineAssignmentData.forEach(a => {
			if (a && a.ed && a.ed < now && !a.bok && !a.answers && !a.submitData && !a.va) {
				// console.log('clearing old', a.aid);
				prms = prms.then(() => clearAssignment(a));
			}
		});
		return prms;
	});
};

const studentAnswerOfflineQuestion = data => {
	let aid = data ? data.assignmentId : null;
	let answer = data ? data.answer : null;

	if (!aid || !answer || !answer.qsid || !answer.qid) {
		throw new Error('Missing data on studentAnswerOfflineQuestion');
	}

	return getAssignmentData(aid).then(assignmentData => {
		if (assignmentData) {
			let answers = assignmentData.answers || {};
			if (answers[answer.qsid]) {
				answers[answer.qsid][answer.qid] = data;
			} else {
				answers[answer.qsid] = {
					[answer.qid]: data
				};
			}
			assignmentData.answers = answers;
			return setAssignmentData(assignmentData).then(() => setOfflineWorkspaceData(true));
		}
		return Q(assignmentData);
	});
};

const studentSaveOfflineBoK = data => {
	let aid = data ? data.aid : null;
	let pid = data ? data.pid : null;
	let bok = data ? data.bok : null;

	if (!aid || !bok || !pid) {
		throw new Error('Missing data on studentAnswerOfflineQuestion');
	}

	return getAssignmentData(aid).then(assignmentData => {
		if (assignmentData) {
			let bokData = assignmentData.bok;
			if (bokData) {
				bokData[pid] = data;
			} else {
				bokData = {
					[pid]: data
				};
			}
			assignmentData.bok = bokData;
			return setAssignmentData(assignmentData).then(() => setOfflineWorkspaceData(true));
		}
		return Q(assignmentData);
	});
};

const studentSubmitOfflineAssignment = data => {
	let aid = data ? data.aid : null;

	if (!aid) {
		throw new Error('Invalid data on studentSubmitOfflineAssignment');
	}

	return getAssignmentData(aid).then(assignmentData => {
		if (assignmentData) {
			if (assignmentData.pmd.pt === 'AAD') {
				let pid = data ? data.pid : null;
				if (!pid) {
					throw new Error('Invalid data on studentSubmitOfflineAssignment');
				}
				if (assignmentData.submitData) {
					assignmentData.submitData[pid] = data;
				} else {
					assignmentData.submitData = {
						[pid]: data
					};
				}
			} else {
				assignmentData.submitData = data;
			}

			fireEvent(events.SUBMIT_OFFLINE_ASSIGNMENT, { aid });

			return setAssignmentData(assignmentData).then(() => setOfflineWorkspaceData(true));
		}
		return Q(assignmentData);
	});
};

const studentRateWordsOffline = (aid, ratings, type) => {
	if (!aid || !ratings || !type) {
		throw new Error('Missing data on studentRateWordsOffline');
	}

	return getAssignmentData(aid).then(assignmentData => {
		if (assignmentData) {
			let activity = assignmentData.va || {};
			activity[type] = ratings;
			assignmentData.va = activity;
			return setAssignmentData(assignmentData).then(() => setOfflineWorkspaceData(true));
		}
		return Q(assignmentData);
	});
};

const studentCompleteOfflineVocabActivity = data => {
	let aid = data ? data.aid : null;
	let type = data ? data.type : null;

	if (!aid || !type) {
		throw new Error('Missing data on studentRateWordsOffline');
	}

	return getAssignmentData(aid).then(assignmentData => {
		if (assignmentData) {
			let activity = assignmentData.va || {};
			activity[type] = data;
			assignmentData.va = activity;
			return setAssignmentData(assignmentData).then(() => setOfflineWorkspaceData(true));
		}
		return Q(assignmentData);
	});
};

const updateWorkspaceWithOfflineData = workspace => {
	if (!available) {
		return Q(workspace);
	}

	return getAssignmentData(workspace.aid).then(assignmentData => {
		if (assignmentData) {
			if (assignmentData.answers) {
				Object.keys(assignmentData.answers).forEach(qsid => {
					Object.keys(assignmentData.answers[qsid]).forEach(qid => {
						let answer = assignmentData.answers[qsid][qid].answer;
						workspace.scoreState.q[qsid][qid] = _.assign({ s: 0 }, answer);
					});
				});
			}
			if (assignmentData.bok) {
				Object.keys(assignmentData.bok).forEach(pid => {
					workspace.scoreState.a[pid] = _.assign({}, workspace.scoreState.a[pid], {
						b: assignmentData.bok[pid].bok,
						tf: assignmentData.bok[pid].ts
					});
				});
			}
			if (assignmentData.va) {
				Object.keys(assignmentData.va).forEach(v => {
					//RTW
					if (v === 'pre' || v === 'post') {
						let ratings = assignmentData.va[v];
						ratings.forEach(r => {
							workspace.scoreState.va.rtw[v][r.word] = r.rating;
						});
					} else {
						//Completed activity - wb, wc, wm
						workspace.scoreState.va[v] = true;
					}
				});
			}
			if (assignmentData.submitData) {
				if (assignmentData.pmd.pt === 'AAD') {
					Object.keys(assignmentData.submitData).forEach(pid => {
						workspace.scoreState.a[pid] = _.assign({}, workspace.scoreState.a[pid], {
							b: assignmentData.submitData[pid].bok,
							tf: assignmentData.submitData[pid].ts,
							s: 's'
						});
					});
				} else {
					workspace.s = 's';
				}
			}
		}
		return _.assign({}, workspace);
	});
};

const uploadOfflineWorkspaces = () => {
	let localGCAccessToken = model.get('localGCAccessToken');
	let workspaces = null;
	let offlineAssignments = null;
	let offlineTs = null;

	if (!available) {
		return Q();
	}
	return Q.all([getAllOfflineAssignmentData(), get('offlineTs')])
		.spread((offlineAssignmentData, ts) => {
			offlineTs = ts;
			offlineAssignments = offlineAssignmentData;
			let workspacePromises = offlineAssignments.map(a => {
				return assignmentService.studentOpenWorkspace({ aid: a.aid }).catch(e => {
					if (e && e.status === 409 && (e.code === 1068 || e.code === 1023)) {
						return null;
					}
					throw e;
				});
			});
			return Q.all(workspacePromises);
		})
		.then(workspaceData => {
			workspaces = workspaceData.filter(w => w);
			return workspaces;
		})
		.then(() => {
			let workspaceMap = workspaces.reduce((acc, ws) => {
				acc[ws.aid] = ws;
				return acc;
			}, {});
			let assignmentPrmsList = offlineAssignments.map(a => {
				let prms = Q();
				let token = undefined;
				let workspace = workspaceMap[a.aid];
				let hasWork = !!a.answers || !!a.bok || !!a.submitData || !!a.va;
				// console.log('uploading', a.aid);
				if (!workspace) {
					return clearAssignment(a);
				} else if (!hasWork) {
					// console.log('No work done on ', a.aid, 'skipping upload / clear');
					return prms;
				}

				if (workspace && workspace.u <= offlineTs && workspace.s !== 's') {
					if (a.gcid && !localGCAccessToken) {
						throw new Error('Needs Google Auth');
					} else if (a.gcid) {
						token = localGCAccessToken;
					}
					// console.log('uploading - ws check pass', a.aid);
					// console.log('uploading', a.aid, a.answers, a.bok, a.submitData);
					//Ensure workspace hasn't been updated since download.
					if (a.pmd.pt === 'AAD') {
						// console.log('uploading - aad', a.aid, a.bok, a.submitData);
						let pids = Object.keys(a.pmd.articles);
						pids.forEach(pid => {
							// For AAD - if submitted, no need to save drafts
							if (a.submitData && a.submitData[pid]) {
								let params = {
									aid: a.submitData[pid].aid,
									pid: a.submitData[pid].pid,
									bok: a.submitData[pid].bok,
									token
								};
								prms = prms.then(() => assignmentService.finishStudentArticle(params));
								// .then(console.log('submitted AAD', a));
							} else if (a.bok && a.bok[pid]) {
								let params = {
									aid: a.bok[pid].aid,
									pid: a.bok[pid].pid,
									bok: a.bok[pid].bok
								};
								prms = prms.then(() => assignmentService.saveBookOfKnowledge(params));
								// .then(console.log('saveBoK', a));
							}
						});
					} else {
						// console.log('uploading', a.aid, a.answers, a.submitData);
						if (a.answers) {
							Object.keys(a.answers).forEach(qsid => {
								let qset = a.answers[qsid];
								Object.keys(qset).forEach(qid => {
									let answer = qset[qid];
									prms = prms.then(() => {
										// console.log('answering', a.aid, answer);
										return assignmentService.studentAnswerQuestion(answer);
									});
									// .then(() => console.log('answered', answer));
								});
							});
						}
						if (a.va) {
							let activity = _.assign({}, a.va);
							let pre = activity.pre || [];
							let post = activity.post || [];
							let ratings = pre.concat(post);
							delete activity.pre;
							delete activity.post;
							let submitVATypes = Object.keys(activity);
							if (ratings && ratings.length) {
								ratings.forEach(r => {
									prms = prms.then(() => assignmentService.rateTheWord(r));
								});
							}
							if (submitVATypes && submitVATypes.length) {
								submitVATypes.forEach(t => {
									let data = activity[t];
									prms = prms.then(() => assignmentService.completeVocabActivity(data));
								});
							}
						}
						if (a.submitData) {
							prms = prms.then(() => {
								// console.log('submitting', a.aid);
								return assignmentService.studentSubmitAssignment({
									aid: a.submitData.aid,
									token
								});
							});
							// .then(() => console.log('submitted', a.submitData));
						}
					}
				} else {
					// console.log('uploading - ws check FAIL', a.aid, a.wu, workspace.u, workspace.s);
				}
				prms = prms.then(() => clearAssignment(a));
				// .then(() => console.log('cleared', a));
				return prms;
			});
			assignmentPrmsList = assignmentPrmsList || [];
			assignmentPrmsList.push(uploadOfflineEvents());
			return Q.all(assignmentPrmsList)
				.then(() => clearOldAssignments())
				.catch(e => {
					let failures = model.get('workspaceUploadFailures') || 0;
					failures++;
					model.set('workspaceUploadFailures', failures);
					throw e;
				})
				.finally(() => checkForOfflineWorkspaceData());
		});
};

const getOfflineModeStatus = () => {
	if (!available) {
		return Q({});
	}

	return Q.all([getOfflineModeSetting(), get('offlineTs'), getOfflineWorkspaceData()]).spread((mode, ts, data) => {
		return {
			mode,
			ts,
			data
		};
	});
};

const clearAll = () => {
	if (!available) {
		return Q();
	}

	// Clear All App Cache
	sendOfflineMessage({ type: 'clear-all' });

	return Q.all([
		Q.delay(1000).then(() => {
			// Give SW a bit of time to clear cache
			// Unregister SW
			navigator.serviceWorker.ready.then(s => s && s.unregister());
		}),
		clear() // Clear IDB
	]).then(() => window.location.reload());
};

const enableOfflineMode = () => {
	let mode = true;
	// Ensure all core assets are cached before allowing offline mode
	return checkForServiceWorker()
		.then(() => checkCore())
		.then(() => clearOldAssignments())
		.then(() => {
			sendOfflineModeToWorker(mode);
			fireEvent(events.OLM_ON);
			return Q.all([
				set('offlineMode', mode),
				set('offlineUserContext', model.get('userContext')),
				set('offlineTs', Date.now()),
				setOfflineWorkspaceData(false)
			]);
		});
};

const disableOfflineMode = () => {
	let mode = false;
	let prms = Q.all([set('offlineMode', mode), remove('offlineUserContext')]);
	sendOfflineModeToWorker(mode);
	fireEvent(events.OLM_OFF);

	return prms;
};

const checkOfflineSupport = () => {
	try {
		if (available) {
			// console.log('Tech present, testing IDB');
			return get('offlineModeAvailable')
				.then(a => set('offlineModeAvailable', available))
				.then(() => available)
				.catch(e => {
					available = false;
					model.set('offlineModeAvailable', false);
					throw e;
				});
		}
	} catch (e) {
		// console.log('Not available');
	}
	available = false;
	model.set('offlineModeAvailable', false);
	return Q(false);
};

// On userContext change (log in / out)
// update status fields
model.change('userContext', function (uc) {
	if (uc) {
		getOfflineModeStatus();
		checkForOfflineWorkspaceData();
	}
});
model.change('idbWriteable', writeable => {
	if (writeable === false) {
		available = false;
		model.set('offlineModeAvailable', false);
	} else {
		checkOfflineSupport();
	}
});

// getOfflineModeSetting(); // handled in vue-app.js#34
// // update existing SW.
checkOfflineSupport().then(support => updateServiceWorkerIfInstalled());

module.exports = {
	getOfflineModeSetting,
	getOfflineModeStatus,
	getOfflineUserContext,
	getOfflineAssignments,

	removeOfflineUserContext,

	downloadAssignment,
	clearAssignment,
	clearOldAssignments,
	getAssignmentData,
	getAllOfflineAssignmentData,
	updateOfflineAssignments,

	enableOfflineMode,
	disableOfflineMode,

	studentAnswerOfflineQuestion,
	studentSaveOfflineBoK,
	studentSubmitOfflineAssignment,
	studentRateWordsOffline,
	studentCompleteOfflineVocabActivity,

	updateOfflineWorkspace,
	updateWorkspaceWithOfflineData,
	uploadOfflineWorkspaces,

	clearAll
};
